import { useEffect, useRef, useState } from 'react';

interface UseAsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseAsyncResult<T> extends UseAsyncState<T> {
  refetch: () => void;
}

/**
 * Runs an async function on mount and whenever `deps` change, tracking
 * loading/error/data and guarding against state updates after unmount.
 * Replaces the repeated useState + try/catch/finally + setLoading boilerplate.
 *
 *   const { data, loading, error, refetch } = useAsync(
 *     () => api.getThing(id).then((r) => r.data),
 *     [id],
 *   );
 */
export function useAsync<T>(
  asyncFn: () => Promise<T>,
  deps: ReadonlyArray<unknown> = [],
): UseAsyncResult<T> {
  const [state, setState] = useState<UseAsyncState<T>>({
    data: null,
    loading: true,
    error: null,
  });
  const [tick, setTick] = useState(0);

  // Always call the latest fn without making it a dependency.
  const fnRef = useRef(asyncFn);
  fnRef.current = asyncFn;

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    fnRef
      .current()
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((e) => {
        if (!cancelled) {
          setState((s) => ({
            ...s,
            loading: false,
            error: e?.message ?? 'Request failed',
          }));
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { ...state, refetch: () => setTick((t) => t + 1) };
}
